import { useEffect, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { findNoteInSequence}  from "$utils/misc";
import { selectTransportCurrentStep } from '../store';
import { ScrollArea } from "../components/ScrollArea";
import { findNoteIndexInSequence } from "../utils/misc";
import { staticSelect } from "../utils/react";
import { scale, clamp } from "$utils/math";
import { FloatingValue } from "../components/FloatingValue";

export const GrooveboxSequencer = ({ className, instrument }) => {
    const numSteps =  useSelector(instrument.getDisplayClipLengthSelector());
    
    const wrapperRef = useRef();
    
    const stepsPerBeat = 4;
    const beatsPerBar = 4;
    const bars = numSteps/stepsPerBeat/beatsPerBar;
    return <div data-widget-id="grooveboxSequencer" ref={wrapperRef} className={`groovebox-sequencer-wrapper ${className}`}>
        <div className={`groovebox-sequencer ${bars === 1 ? "expanded" : ""}`} style={{"--num-steps": numSteps}}>
            <ScrollArea scrollElementRef={wrapperRef} style={{position: "absolute", inset: 0, height: "fit-content"}}>
                <div style={{height: 28}} />
            </ScrollArea>
            { Array(bars).fill().map((_, barIndex) => <div className="bar-and-timeline" key={barIndex}>
                {Array(beatsPerBar).fill().map((_, beatInBarIndex) => <div className="beat-and-timeline" key={beatInBarIndex}>
                    <div>{`${barIndex+1}.${beatInBarIndex+1}`}</div>
                    <div className="beat-group" style={{"--steps-per-group": stepsPerBeat}}>
                        {instrument.playerNames.map(playerName => {
                            const pitch = instrument.getPlayerPitch(playerName);
                            return <div className="beat-group-row" key={playerName} style={{ "--accent-color": instrument.getPlayerColor(playerName)}}>
                                {Array(stepsPerBeat).fill().map((_, stepIndexInBeatGroup) => {
                                    const stepIndex = (barIndex * beatsPerBar + beatInBarIndex) * stepsPerBeat + stepIndexInBeatGroup;
                                    return <GrooveboxStep
                                        key={`${pitch}_${stepIndex}`}
                                        instrument={instrument}
                                        numSteps={numSteps}
                                        pitch={pitch}
                                        stepIndex={stepIndex}
                                    />
                                })}
                            </div>})
                        }
                    </div>
                </div>)}
            </div>) }
        </div>
    </div>
};

function GrooveboxStep({instrument, numSteps, pitch, stepIndex}) {
    const currentStep = useSelector(createSelector(selectTransportCurrentStep, currentStep => currentStep % numSteps));
    const sequence = useSelector(instrument.getDisplaySequenceSelector());
    const displayClipNumber = useSelector(instrument.getDisplayParameterSelector("clipNumber"));
    const playbackClipNumber = useSelector(instrument.getPlaybackParameterSelector("clipNumber"));

    const targetProperties = {pitch, onset: stepIndex};
    const yOffsetRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    function handleClick() {
        // we can't use the state because this lives in an envent handler
        const isDragging = yOffsetRef.current != null;
        if (isDragging) return;
        let updatedSequence = [...sequence];
        const index = findNoteIndexInSequence(sequence, targetProperties);
        if (index !== -1){
            updatedSequence.splice(index, 1);
        } else {
            updatedSequence.push({...targetProperties, duration: 1});
        }
        instrument.updateSequence(updatedSequence);
    }
    function handlePointerDown(downE, note) {
        const controller = new AbortController();
        const initialVelocity = note ? note.velocity : 1;
        const stopDrag = () => {
            controller.abort();
            yOffsetRef.current = null;
            setIsDragging(false);
        }
        document.addEventListener("pointermove", e => handleDrag(e, downE, initialVelocity ?? 127), {signal: controller.signal});
        document.addEventListener("pointerup", stopDrag, { signal: controller.signal});
        document.addEventListener("pointercancel", stopDrag, { signal: controller.signal});
    }
    function handleDrag(e, downE, initialVelocity) {
        const threshold = 10;
        let movementY = e.clientY - downE.clientY;
        if (yOffsetRef.current == null) {
            // si el moviment és molt petit probablement l'usuari volia fer click i no drag
            if (Math.abs(movementY) < threshold) return;
            yOffsetRef.current = - movementY;
            setIsDragging(true);
        }
        movementY += yOffsetRef.current;
        const latestSequence = [...staticSelect(instrument.getDisplaySequenceSelector())];
        const index = findNoteIndexInSequence(latestSequence, targetProperties);
        const note = latestSequence[index] ?? {...targetProperties, duration: 1 };
        const velocity = Math.round(clamp(initialVelocity - movementY, 1, 127));
        if (index !== -1) latestSequence.splice(index, 1);
        latestSequence.push({...note, velocity});
        instrument.updateSequence(latestSequence);
    }
    const note = findNoteInSequence(sequence, targetProperties);
    const velocity = note ? note.velocity ?? 127 : null;
    const isFilled = note != null;
    const opacity = isFilled ? scale(velocity, 0, 127, .3, 1) : 1;
    const isActive = (currentStep == stepIndex && displayClipNumber === playbackClipNumber);
    return <FloatingValue value={velocity} isVisible={isDragging}>
        <div onClick={handleClick}
        onPointerDown={downE => handlePointerDown(downE, note)}
        className={`step ${isFilled ? 'selected' : ''} ${isActive ? 'active' : ''}`}
        style={{opacity}}
        />
    </FloatingValue>
}